import React, { FC, useEffect, useState } from "react";
import { useDataProvider, useInput } from "react-admin";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { find } from "lodash";
import { useField } from "react-final-form";
import { CircularProgress, makeStyles } from "@material-ui/core";
import { getFixedLocationDropOffBySchoolIds } from "../tools";

interface StudentAddressAutoCompleteInputProps {
  label: string;
  studentId: number;
  referenceSource: string;
  source: string;
}

const useStyles = makeStyles({
  root: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      padding: "6px",
      borderRadius: "10px",
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

const StudentAddressAutoCompleteInput: FC<
  StudentAddressAutoCompleteInputProps
> = ({ studentId, source, label, referenceSource }) => {
  const [addressOptions, setAddressOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const classes = useStyles();
  const provider = useDataProvider();

  const busScheduleField = useField("busSchedule");
  const schoolField = useField("school");

  const {
    input: { onChange },
  } = useField(referenceSource);

  const {
    input,
    meta: { touched, error },
  } = useInput({ source });

  const getStudentAddress = async () => {
    try {
      let studentAddress = await provider.getList("studentAddresses", {
        filter: { StudentId: Number(studentId) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      });
      let dataFixLocation: any = [];

      if (busScheduleField.input.value.type === "cca") {
        dataFixLocation = await getFixedLocationDropOffBySchoolIds(
          schoolField.input.value.id,
          provider,
        );
      }

      setAddressOptions([...studentAddress.data, ...dataFixLocation]);
      setLoading(false);
    } catch (error) {
      console.error("Failed to fetch student addresses:", error);
      setAddressOptions([]); // Fallback to empty array
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    getStudentAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Autocomplete
      className={classes.root}
      options={addressOptions.map((option: any) => {
        let type = "";
        if (option.isMain === true) {
          type = "MAIN";
        } else if (option.isMain === false) {
          type = "SUB";
        } else {
          type = option.name;
        }
        return `${option.id} - ${option.blockNumber} ${option.street} ${option.postalCode} - ${type}`;
      })}
      onChange={(event, value) => {
        input.onChange(value);

        const splitedValue = value.split(" ");

        onChange(find(addressOptions, ["id", Number(splitedValue[0])]));
      }}
      freeSolo
      disableClearable
      value={input.value}
      inputValue={input.value}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          error={!!(touched && error)}
          helperText={touched && error}
          {...input}
          InputProps={{
            ...params.InputProps,
            type: "search",
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

export default StudentAddressAutoCompleteInput;
